// dashboard/src/pages/apps-setup/StoreListingEditor.tsx
//
// The listing text a handoff link serves, editable per locale. Not every app is
// uploaded by us: whoever does upload it reads this off the public page, so the
// package has to be fixable after the wizard generated it.
//
// This is not the Play drafts layer — saving here changes the handoff package
// only. Texts that go to Google are still edited on the Play page.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { ApiError } from '@/api/client';
import {
  getStoreListings, saveStoreListing, deleteStoreListing, translateStoreListing,
  type HandoffListing,
} from '@/api/app-handoff';
import { PLAY_LOCALES, playLocaleName } from '@/lib/playLocales';

/** Play's hard limits; over them the row is saved and flagged, never rejected. */
const LIMITS = { title: 30, short_description: 80, full_description: 4000 };

type Draft = { title: string; short_description: string; full_description: string };

const draftOf = (l: HandoffListing): Draft => ({
  title: l.title,
  short_description: l.short_description,
  full_description: l.full_description,
});

const same = (a: Draft, b: Draft) =>
  a.title === b.title
  && a.short_description === b.short_description
  && a.full_description === b.full_description;

function Counter({ value, max }: { value: string; max: number }) {
  return (
    <span className={`text-xs ${value.length > max ? 'text-red-500' : 'text-content-subtle'}`}>
      {value.length}/{max}
    </span>
  );
}

const inputCls = 'w-full px-3 py-2 bg-background border border-border rounded-md text-content text-sm '
  + 'focus:outline-none focus:ring-2 focus:ring-primary/40';

export function StoreListingEditor({ appId }: { appId: string }) {
  const qc = useQueryClient();
  const listingsQ = useQuery({
    queryKey: ['store-listings', appId],
    queryFn: () => getStoreListings(appId),
  });
  const listings = useMemo(() => listingsQ.data ?? [], [listingsQ.data]);

  // Unsaved edits per locale, so switching chips does not drop typing.
  const [edits, setEdits] = useState<Record<string, Draft>>({});
  const [lang, setLang] = useState('');
  const [addLang, setAddLang] = useState('');
  const [error, setError] = useState('');

  // Locales already in the package — stored ones plus any added but not saved.
  const present = useMemo(() => {
    const codes = listings.map((l) => l.lang);
    for (const code of Object.keys(edits)) if (!codes.includes(code)) codes.push(code);
    return codes;
  }, [listings, edits]);

  // A locale added from the picker is selectable before it has a server row, so
  // the chip list — not `listings` — decides what "selected" can mean.
  const activeLang = present.includes(lang) ? lang : (present[0] ?? '');
  const stored = listings.find((l) => l.lang === activeLang) ?? null;
  const saved = stored ? draftOf(stored) : { title: '', short_description: '', full_description: '' };
  const draft = edits[activeLang] ?? saved;
  const dirty = !same(draft, saved);
  const unsaved = !!activeLang && !stored;

  const set = (patch: Partial<Draft>) =>
    setEdits((prev) => ({ ...prev, [activeLang]: { ...draft, ...patch } }));

  const save = useMutation({
    mutationFn: () => saveStoreListing(appId, activeLang, draft),
    onSuccess: async () => {
      // Await the refetch before dropping the local edit: until the row is in
      // `listings`, the chip exists only because of that edit.
      await qc.invalidateQueries({ queryKey: ['store-listings', appId] });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[activeLang];
        return next;
      });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: (code: string) => deleteStoreListing(appId, code),
    onSuccess: (_r, code) => {
      setEdits((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setLang('');
      qc.invalidateQueries({ queryKey: ['store-listings', appId] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Remove failed'),
  });

  // Transcreation from the source locale. The text lands in the form as an
  // unsaved edit, so it is reviewed (and trimmed, if it overshot) before it
  // reaches the package.
  const sourceLang = (listings.find((l) => l.is_source) ?? listings[0])?.lang ?? '';
  const translate = useMutation({
    mutationFn: (target: string) => translateStoreListing(appId, target, sourceLang || undefined),
    onSuccess: (out) => setEdits((prev) => ({
      ...prev,
      [out.lang]: {
        title: out.title,
        short_description: out.short_description,
        full_description: out.full_description,
      },
    })),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Translation failed'),
  });

  const addOptions = useMemo(
    () => PLAY_LOCALES.filter((l) => !present.includes(l.code))
      .map((l) => ({ value: l.code, label: `${l.name} · ${l.code}` })),
    [present],
  );

  function addLocale(code: string) {
    if (!code) return;
    setAddLang('');
    setError('');
    // Seeded from the source text: a translator edits over it rather than
    // starting from an empty screen.
    const source = listings.find((l) => l.is_source) ?? listings[0];
    setEdits((prev) => ({
      ...prev,
      [code]: prev[code] ?? (source ? draftOf(source) : { title: '', short_description: '', full_description: '' }),
    }));
    setLang(code);
  }

  if (listingsQ.isLoading) {
    return <p className="text-xs text-content-subtle">Loading the listing…</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-content">Store listing ({present.length} locale(s))</h4>
        <div className="w-56">
          <Select
            value={addLang}
            options={addOptions}
            onChange={addLocale}
            placeholder="+ Add locale"
            searchable
            className="w-full"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {present.map((code) => {
          const row = listings.find((l) => l.lang === code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => { setError(''); setLang(code); }}
              title={playLocaleName(code) ?? code}
              className={`rounded-md border px-2 py-1 text-xs ${
                code === activeLang
                  ? 'border-primary bg-primary text-white'
                  : 'border-border bg-background text-content-muted hover:text-content'
              }`}
            >
              {code}
              {row?.is_source ? ' · source' : ''}
              {edits[code] ? ' •' : ''}
              {row && !row.fits ? ' ⚠' : ''}
            </button>
          );
        })}
      </div>

      {activeLang ? (
        <div className="space-y-2 rounded-md border border-border bg-background/40 p-3">
          {unsaved && (
            <p className="text-xs text-content-subtle">
              New locale — nothing is stored until you save it.
            </p>
          )}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs text-content-muted">App name</label>
              <Counter value={draft.title} max={LIMITS.title} />
            </div>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => set({ title: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs text-content-muted">Short description</label>
              <Counter value={draft.short_description} max={LIMITS.short_description} />
            </div>
            <textarea
              rows={2}
              value={draft.short_description}
              onChange={(e) => set({ short_description: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs text-content-muted">Full description</label>
              <Counter value={draft.full_description} max={LIMITS.full_description} />
            </div>
            <textarea
              rows={8}
              value={draft.full_description}
              onChange={(e) => set({ full_description: e.target.value })}
              className={inputCls}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              disabled={!dirty && !unsaved}
              loading={save.isPending}
              onClick={() => { setError(''); save.mutate(); }}
            >
              Save {activeLang}
            </Button>
            {!!sourceLang && activeLang !== sourceLang && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                loading={translate.isPending}
                onClick={() => { setError(''); translate.mutate(activeLang); }}
              >
                Translate from {sourceLang}
              </Button>
            )}
            {stored && !stored.is_source && (
              <button
                type="button"
                onClick={() => { setError(''); remove.mutate(activeLang); }}
                className="text-xs text-content-muted hover:text-red-500"
              >
                Remove locale
              </button>
            )}
            {stored && !stored.fits && (
              <span className="text-xs text-amber-500">
                Over Google Play's limits — trim it before publishing.
              </span>
            )}
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      ) : (
        <p className="text-xs text-content-subtle">No listing text for this app yet.</p>
      )}
    </div>
  );
}
